import type {
  GetMySignatureApprovalResponse,
  GetMySignatureApprovalsResponse,
  GetMySignatureAnswersFormResponse,
  GetMySignatureRequestResponse,
  GetMySignatureRequestsResponse,
  GetSignatureGateResponse,
  GetSignatureUrlResponse,
  SignatureFileKind,
} from '@gokrew/models/signature-requests';
import api from './client';

const endpoints = {
  mine: '/employee/signature-requests',
  one: (id: string) => `/employee/signature-requests/${id}`,
  continue: (id: string) => `/employee/signature-requests/${id}/continue`,
  answers: (id: string) => `/employee/signature-requests/${id}/answers`,
  file: (id: string, kind: SignatureFileKind) =>
    `/employee/signature-requests/${id}/files/${kind}`,
  approvals: '/employee/signature-approvals',
  approval: (id: string) => `/employee/signature-approvals/${id}`,
  gate: (token: string) => `/esignature/gate/${encodeURIComponent(token)}`,
  gateCode: (token: string) =>
    `/esignature/gate/${encodeURIComponent(token)}/code`,
  gateVerify: (token: string) =>
    `/esignature/gate/${encodeURIComponent(token)}/verify`,
};

// ─── My documents to sign ────────────────────────────────────────────────────

export async function getMySignatureRequests() {
  const { data } = await api.get<GetMySignatureRequestsResponse>(
    endpoints.mine
  );
  return data.data;
}

export async function getMySignatureRequest(id: string) {
  const { data } = await api.get<GetMySignatureRequestResponse>(
    endpoints.one(id),
    { quietStatuses: [404] }
  );
  return data.data;
}

/** My own signing URL, on my turn only; the page opens it as its own page. */
export async function continueMySignature(id: string) {
  const { data } = await api.post<GetSignatureUrlResponse>(
    endpoints.continue(id),
    {},
    // 422 ANSWERS_REQUIRED: the page opens the fields to fill in first.
    { quietStatuses: [404, 409, 422] }
  );
  return data.data;
}

/** The fields I fill in before signing, prefilled from my profile. */
export async function getMySignatureAnswers(id: string) {
  const { data } = await api.get<GetMySignatureAnswersFormResponse>(
    endpoints.answers(id),
    { quietStatuses: [404] }
  );
  return data.data;
}

/** My answers; the reply is my signing page's URL, on my turn only. */
export async function submitMySignatureAnswers(
  id: string,
  answers: Record<string, unknown>
) {
  const { data } = await api.post<GetSignatureUrlResponse>(
    endpoints.answers(id),
    { answers },
    { quietStatuses: [422] }
  );
  return data.data;
}

export async function getMySignatureFile(id: string, kind: SignatureFileKind) {
  const { data } = await api.get<GetSignatureUrlResponse>(
    endpoints.file(id, kind)
  );
  return data.data;
}

// ─── The public signing gate (emailed links) ─────────────────────────────────

export async function openSignatureGate(token: string) {
  const { data } = await api.get<GetSignatureGateResponse>(
    endpoints.gate(token),
    { quietStatuses: [404] }
  );
  return data.data;
}

export async function sendSignatureGateCode(token: string) {
  const { data } = await api.post<GetSignatureGateResponse>(
    endpoints.gateCode(token),
    {},
    { quietStatuses: [404, 429] }
  );
  return data.data;
}

export async function verifySignatureGateCode(token: string, code: string) {
  const { data } = await api.post<GetSignatureUrlResponse>(
    endpoints.gateVerify(token),
    { code },
    { quietStatuses: [404, 409, 422, 429] }
  );
  return data.data;
}

// ─── Documents I approve before they go to an employee (2026-10-15) ─────────

export async function getMySignatureApprovals() {
  const { data } = await api.get<GetMySignatureApprovalsResponse>(
    endpoints.approvals
  );
  return data.data;
}

export async function getMySignatureApproval(id: string) {
  const { data } = await api.get<GetMySignatureApprovalResponse>(
    endpoints.approval(id),
    { quietStatuses: [404] }
  );
  return data.data;
}

export async function approveMySignatureApproval(id: string) {
  const { data } = await api.post<GetMySignatureApprovalResponse>(
    `${endpoints.approval(id)}/approve`,
    {}
  );
  return data.data;
}

export async function requestChangesOnMySignatureApproval(
  id: string,
  comment: string
) {
  const { data } = await api.post<GetMySignatureApprovalResponse>(
    `${endpoints.approval(id)}/request-changes`,
    { comment }
  );
  return data.data;
}
